/**
 * Reference Data Sync Service
 * Downloads and caches reference data tables in IndexedDB for offline access.
 *
 * Synchronization strategy:
 * 1. On first launch (after auth): download all reference tables
 * 2. On each "back online" event: check sync-status endpoint, re-download only stale tables
 * 3. Data is immediately available from IndexedDB cache at app startup, no network wait
 */

import { getApiBaseUrl } from '../config/env';
import api from './api';
import {
  getReferenceData,
  getReferenceMetadata,
  getAllReferenceMetadata,
  putReferenceData,
  putReferenceMetadata,
  putCachedSearch,
  putConfig,
} from './offlineDb';
import type { FreightLotSearchResult } from './checkpointPassages';

// ─── Table definitions ─────────────────────────────────────────────────────────

export interface ReferenceTableDef {
  /** Key used in IndexedDB and sync-status response */
  name: string;
  /** API endpoint path (relative to /api/v1) */
  endpoint: string;
}

/**
 * All reference tables that should be cached offline.
 * Each entry maps to a non-paginated "getAll" endpoint that returns an array.
 */
export const REFERENCE_TABLES: ReferenceTableDef[] = [
  { name: 'countries', endpoint: '/countries/' },
  { name: 'vehicle_types', endpoint: '/vehicle-types/' },
  { name: 'checkpoints', endpoint: '/checkpoints/' },
  { name: 'itineraries', endpoint: '/itineraries/' },
  { name: 'parameters', endpoint: '/parameters/' },
  { name: 'insurers', endpoint: '/insurers/' },
  { name: 'currencies', endpoint: '/currencies/' },
];

// ─── Sync status from backend ──────────────────────────────────────────────────

interface SyncStatusResponse {
  tables: Record<string, string | null>; // tableName → ISO timestamp of last update
}

/**
 * Fetches the last-updated timestamps for all reference tables from the backend.
 * Returns null if the endpoint fails (e.g. offline or not yet deployed).
 */
async function fetchSyncStatus(): Promise<SyncStatusResponse | null> {
  try {
    const res = await api.get<SyncStatusResponse>(
      '/reference-data/sync-status'
    );
    return res.data;
  } catch {
    return null;
  }
}

// ─── Core sync logic ───────────────────────────────────────────────────────────

/**
 * Synchronise a single reference table:
 * - Fetch the full list from the API
 * - Store in IndexedDB
 * - Update metadata with current timestamp
 */
async function syncTable(table: ReferenceTableDef): Promise<boolean> {
  try {
    const res = await api.get<unknown[]>(table.endpoint);
    const data = Array.isArray(res.data) ? res.data : [];
    await putReferenceData(table.name, data);
    await putReferenceMetadata(table.name, {
      lastSync: Date.now(),
      count: data.length,
    });
    return true;
  } catch {
    // Network error or API error — skip this table
    return false;
  }
}

/**
 * Synchronise all reference tables.
 *
 * Strategy:
 * 1. Try to fetch sync-status from backend to know which tables changed
 * 2. For each table, compare server's last_updated with our local lastSync
 * 3. Only re-download tables that are stale (or never synced)
 * 4. If sync-status endpoint is unavailable, fall back to syncing everything
 *
 * Returns the number of tables successfully synced.
 */
export async function syncAll(): Promise<number> {
  // Persist the API base URL in IndexedDB so the Service Worker can read it
  // for background sync replay (the SW cannot access window.__ENV__ or import.meta.env).
  try {
    await putConfig('apiBaseUrl', getApiBaseUrl());
  } catch {
    // Non-fatal: SW will fall back to scope-based URL
  }

  const syncStatus = await fetchSyncStatus();
  const localMeta = await getAllReferenceMetadata();

  let synced = 0;

  const promises = REFERENCE_TABLES.map(async (table) => {
    const localLastSync = localMeta[table.name]?.lastSync ?? 0;

    if (syncStatus) {
      // Incremental: only sync if server reports a newer timestamp
      const serverTs = syncStatus.tables[table.name];
      if (serverTs) {
        const serverTime = new Date(serverTs).getTime();
        if (serverTime <= localLastSync) {
          // Local cache is up-to-date
          return;
        }
      } else if (localLastSync > 0) {
        // Server didn't report this table but we have a cached version — skip
        return;
      }
    }
    // Either no sync-status available, or table is stale → download
    const ok = await syncTable(table);
    if (ok) synced++;
  });

  await Promise.allSettled(promises);

  // Pre-cache LVO data and recent passages for offline checkpoint search
  await Promise.allSettled([
    syncOfflineCache(),
    syncRecentPassages(),
  ]);

  return synced;
}

// ─── Cache access ──────────────────────────────────────────────────────────────

/**
 * Retrieve cached reference data from IndexedDB.
 * Returns undefined if the table has never been synced.
 */
export async function getFromCache<T = unknown>(
  tableName: string
): Promise<T[] | undefined> {
  const data = await getReferenceData(tableName);
  return data as T[] | undefined;
}

/**
 * Returns the local last-sync timestamp for a given table (epoch ms), or 0 if never synced.
 */
export async function getLastSyncTime(tableName: string): Promise<number> {
  const meta = await getReferenceMetadata(tableName);
  return meta?.lastSync ?? 0;
}

/**
 * Returns true if at least one full sync has been completed (all tables present).
 */
export async function hasCompletedInitialSync(): Promise<boolean> {
  const meta = await getAllReferenceMetadata();
  return REFERENCE_TABLES.every((t) => meta[t.name]?.lastSync > 0);
}

// ─── Offline LVO pre-caching ─────────────────────────────────────────────────

interface OfflineCacheResponse {
  items: FreightLotSearchResult[];
  checkpoint_name: string | null;
  cached_at: string;
}

/**
 * Fetch all active freight lots (with LVO) from the backend and store each one
 * as a cached search entry in IndexedDB, keyed by LVO reference, vehicle
 * registration numbers, and VIN numbers.
 *
 * This ensures the checkpoint agent can search for any active LVO offline.
 */
async function syncOfflineCache(): Promise<void> {
  try {
    const res = await api.get<OfflineCacheResponse>(
      '/checkpoint-passages/offline-cache',
    );
    const { items } = res.data;
    if (!Array.isArray(items)) return;

    const now = Date.now();

    for (const item of items) {
      const cachePayload = { result: item, cachedAt: now };

      // Cache by LVO reference
      if (item.lvo_reference) {
        await putCachedSearch(
          `search:lvo:${item.lvo_reference.trim().toUpperCase()}`,
          cachePayload,
        );
      }

      // Cache by each vehicle registration number and VIN
      for (const v of item.vehicles ?? []) {
        if (v.registration_number) {
          await putCachedSearch(
            `search:reg:${v.registration_number.trim().toUpperCase()}`,
            cachePayload,
          );
        }
        if (v.vin_number) {
          await putCachedSearch(
            `search:vin:${v.vin_number.trim().toUpperCase()}`,
            cachePayload,
          );
        }
      }
    }

    await putReferenceMetadata('offline-cache', {
      lastSync: now,
      count: items.length,
    });
  } catch {
    // Non-fatal: offline cache pre-loading failed — user can still search if previously cached
  }
}

// ─── Recent passages pre-caching ──────────────────────────────────────────────

/**
 * Fetch today's checkpoint passages and store them in reference-data so the
 * passages table can render offline.
 */
async function syncRecentPassages(): Promise<void> {
  try {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).toISOString();

    const res = await api.get('/checkpoint-passages/', {
      params: { page: 1, size: 100, date_from: todayStart, date_to: todayEnd },
    });

    if (res.data) {
      await putReferenceData('recent-passages', [res.data]);
      await putReferenceMetadata('recent-passages', {
        lastSync: Date.now(),
        count: res.data.items?.length ?? 0,
      });
    }
  } catch {
    // Non-fatal
  }
}
